import moment from "moment";
import React, { useCallback, useEffect, useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Inputs,
  PaginationComponent,
  Spinner,
  Tables,
} from "../../../Components";
import { TableActions } from "../../../Enums";
import { bottomBoxComponentUpdate, showError } from "../../../Helper";

import { ButtonBase } from "@material-ui/core";
import { GetAllAwards } from "../../../Services/AwardServices";
import "./AwardManagementLNDView.scss";
import CreateNewAwardDialog from "./UI/CreateNewAwardDialog";
import DeleteUserAward from "./UI/DeleteAward";
import EditAward from "./UI/EditAward";
import ViewAwardDialog from "./UI/ViewAwardDialog";

const translationPath = "Awards";
function AwardManagementLNDView() {
  const { t } = useTranslation(translationPath);
  const reducer = useCallback((state, action) => {
    if (action.id !== "edit") return { ...state, [action.id]: action.value };
    return {
      ...action.value,
    };
  }, []);

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeAward, setActiveAward] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [filter, setFilter] = useReducer(reducer, {
    pageIndex: 1,
    pageSize: 25,
    awardName: null,
  });

  function tableActionClicked(actionEnum, item, focusedRow, event) {
    event.preventDefault();
    if (actionEnum === TableActions.view.key) {
      setIsViewDialogOpen(true);
    } else if (actionEnum === TableActions.edit.key) {
      setIsEditDialogOpen(true);
    } else if (actionEnum === TableActions.delete.key) {
      setIsDeleteDialogOpen(true);
    }
  }

  const onPageIndexChanged = (pageIndex) => {
    setFilter({ id: "pageIndex", value: pageIndex + 1 });
  };
  const onPageSizeChanged = (pageSize) => {
    setFilter({
      id: "edit",
      value: {
        ...filter,
        pageIndex: 1,
        pageSize: pageSize,
      },
    });
  };

  async function getAllUsersAwards() {
    const body = {
      pageIndex: filter.pageIndex,
      pageSize: filter.pageSize,
      awardName: filter.awardName,
      userId: filter.userId,
    };
    const res = await GetAllAwards(body);

    if (!(res && res.status && res.status !== 200)) {
      setData(res || []);
    } else {
      setData([]);
      showError(t("error-fetching-user-awards"));
    }
    setIsLoading(false);
  }

  function refetchData() {
    getAllUsersAwards();
  }

  useEffect(() => {
    setIsLoading(true);
    getAllUsersAwards();
  }, [filter]);

  useEffect(() => {
    bottomBoxComponentUpdate(
      <PaginationComponent
        pageIndex={filter.pageIndex - 1}
        pageSize={filter.pageSize}
        totalCount={data.totalCount}
        onPageIndexChanged={onPageIndexChanged}
        onPageSizeChanged={onPageSizeChanged}
      />
    );
  });

  useEffect(
    () => () => {
      bottomBoxComponentUpdate(null);
    },
    []
  );

  return (
    <>
      <Spinner isActive={isLoading} isAbsolute />
      <div className="lnd-award-management-view">
        <div className="d-flex">
          <Inputs
            idRef={"awardNameSearchRef"}
            inputPlaceholder={"search-award-name"}
            type={"text"}
            value={filter.awardName || ""}
            onInputChanged={(e) => {
              const value = e.target.value ?? null;
              setFilter({ id: "awardName", value: value });
            }}
            translationPath={""}
            parentTranslationPath={translationPath}
          />

          <ButtonBase
            className="MuiButtonBase-root btns theme-solid "
            style={{ maxHeight: "28px" }}
            onClick={() => {
              setIsCreateDialogOpen(true);
            }}
          >
            {t("create")}
          </ButtonBase>
        </div>
        <Tables
          data={data.result || []}
          headerData={[
            {
              id: 1,
              label: "award-name",
              input: "awardName",
            },
            {
              id: 2,
              label: "award-date",
              component: (item) => (
                <>
                  <span>
                    {moment(item.createdOn).format("DD/MM/YYYY - hh:mm A") ||
                      "N/A"}
                  </span>
                </>
              ),
            },
          ]}
          activePage={0}
          itemsPerPage={filter.pageSize || 0}
          defaultActions={[
            { enum: TableActions.view.key },
            { enum: TableActions.edit.key },
            { enum: TableActions.delete.key },
          ]}
          actionsOptions={{
            onActionClicked: tableActionClicked,
          }}
          focusedRowChanged={(rowIndex, item) => {
            setActiveAward(item);
          }}
          totalItems={data.totalCount || 0}
          translationPath={""}
          parentTranslationPath={translationPath}
        />
      </div>

      {isCreateDialogOpen && (
        <CreateNewAwardDialog
          isOpen={isCreateDialogOpen}
          refetchData={refetchData}
          translationPath={translationPath}
          setIsCreateDialogOpen={setIsCreateDialogOpen}
        />
      )}
      {isDeleteDialogOpen && (
        <DeleteUserAward
          isOpen={isDeleteDialogOpen}
          activeAward={activeAward}
          refetchData={refetchData}
          translationPath={translationPath}
          setIsDeleteDialogOpen={setIsDeleteDialogOpen}
        />
      )}
      {isEditDialogOpen && (
        <EditAward
          isOpen={isEditDialogOpen}
          activeAward={activeAward}
          translationPath={translationPath}
          setIsEditDialogOpen={setIsEditDialogOpen}
          refetchData={refetchData}
        />
      )}
      {isViewDialogOpen && (
        <ViewAwardDialog
          isOpen={isViewDialogOpen}
          activeAward={activeAward}
          translationPath={translationPath}
          setIsViewDialogOpen={setIsViewDialogOpen}
          refetchData={refetchData}
        />
      )}
    </>
  );
}

export default AwardManagementLNDView;
